/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, Input, NgZone, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import * as _ from 'lodash';

import template from './default-item-title.component.html';
import {objectTypeStateParams} from '../common/filters/object-type-state-params';
import {OBJECT_TYPE} from '../common/constants/object-type';
import {calculateCrumbs, updateCrumb} from '../common/controller/crumb-calculator';
import {ModelComms} from '../common/controller/model-comms';
import {CreativeSetInfoResource} from '../../components/api/resources/resource/creative-set.resource';
import {DataViewResource} from '../../components/api/resources/resource/data-view';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';

export interface DefaultItemTitleCrumb {
  id?: string;
  name: string;
  obj: any;
  objectType: any;
  perm?: boolean;
  labelOverride?: string;
}

@Component({
  standalone: false,
  selector: 'adn-default-item-title',
  template
})
export class DefaultItemTitleComponent implements OnInit, OnChanges {
  @Input() model: any;
  @Input() modelComms: ModelComms;
  @Input() crumbParams: any[];
  @Input() titleVariant: string;
  @Input() lineItemInfo: any;
  @Input() folioFromParam: any;
  @Input() advertiserFromParam: any;
  @Input() workspace: any;
  @Input() isNew: boolean;

  private _objectTypeKey: string;

  get objectTypeKey(): string {
    return this._objectTypeKey;
  }

  @Input()
  set objectTypeKey(value: string) {
    this._objectTypeKey = value;
  }

  /** Derived from objectTypeKey rather than stored, so it can never be read stale within a change-detection pass. */
  get objectType(): any {
    return this._objectTypeKey ? OBJECT_TYPE[this._objectTypeKey] : null;
  }

  @Input() itemName = '';
  @Input() crumbs: DefaultItemTitleCrumb[] = [];

  private persisted = true;
  private creativeCrumbsLoaded = false;
  private crumbCtrl: {perms: any; sectionPerms: any};
  private initialized = false;

  constructor(
    @Inject(LocalUserPermissions) private readonly localUserPermissions: any,
    @Inject(DataViewResource) private readonly dataViewResource: any,
    @Inject(CreativeSetInfoResource) private readonly creativeSetInfo: any,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    this.tryInit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.objectTypeKey || changes.model) {
      this.tryInit();
    }
  }

  private tryInit(): void {
    if (this.initialized || !this.objectTypeKey) {
      return;
    }
    this.initialized = true;

    this.crumbCtrl = {
      perms: this.localUserPermissions.getAllVisPermissions(),
      sectionPerms: this.localUserPermissions.getSectionPerms()
    };
    this.persisted = !this.isNew;

    if (this.titleVariant === 'workspace') {
      this.initWorkspaceTitle();
      return;
    }

    this.itemName = this.resolveItemName(this.model);
    this.initVariantCrumbs();

    if (this.modelComms) {
      this.modelComms.addSubscriber((data, type, isNewFlag) => {
        // Defer: modelComms can notify synchronously mid-digest (e.g. a sibling component's
        // resolve settling elsewhere in the same tree walk), and mutating a template-bound
        // property like crumbs right then races Angular's checkNoChanges pass and throws
        // ExpressionChangedAfterItHasBeenCheckedError. Deferring to a new task guarantees the
        // current change-detection cycle has fully finished before this component's state changes.
        setTimeout(() => {
          this.ngZone.run(() => {
            this.onModelCommsUpdate(data, type, isNewFlag);
            this.cdr.markForCheck();
          });
        });
      }, false);
    }
  }

  crumbStateParams(crumb: DefaultItemTitleCrumb): Record<string, any> {
    return objectTypeStateParams(crumb.obj, crumb.objectType, crumb.obj);
  }

  private initWorkspaceTitle(): void {
    const workspace = this.workspace || this.model;
    this.itemName = workspace?.name || 'New';
    if (!this.modelComms) {
      return;
    }
    this.modelComms.addSubscriber((data, type) => {
      if (type) {
        return;
      }
      // See the addSubscriber callback in tryInit() for why this defers.
      setTimeout(() => {
        this.ngZone.run(() => {
          this.itemName = data?.name || 'New';
          this.cdr.markForCheck();
        });
      });
    }, false);
  }

  private onModelCommsUpdate(data: any, type: any, isNewFlag: boolean): void {
    if (this.titleVariant === 'creative-set') {
      if (type) {
        return;
      }
      if (isNewFlag === true || isNewFlag === false) {
        this.persisted = !isNewFlag;
      }
      this.itemName = data?.name || 'New';
      if (_.get(data, ['folio', 'id']) || _.get(data, ['advertiser', 'id'])) {
        this.applyCreativeSetCrumbs(data);
      } else if (this.persisted) {
        this.loadCreativeSetParentCrumbs(data);
      }
      return;
    }

    if (this.titleVariant === 'folio') {
      if (type) {
        return;
      }
      this.itemName = data?.name || 'New';
      this.applyFolioCrumbs(data);
      return;
    }

    if (this.titleVariant === 'library-creative') {
      if (type) {
        return;
      }
      this.itemName = data?.name || 'New';
      return;
    }

    if (this.titleVariant === 'creative') {
      if (type) {
        return;
      }
      if (!_.get(this.model, ['lineItem', 'id']) && _.get(data, ['lineItem', 'id'])) {
        this.model.lineItem = data.lineItem;
      }
      if (!_.get(this.model, ['creativeSet', 'id']) && _.get(data, ['creativeSet', 'id'])) {
        this.model.creativeSet = data.creativeSet;
      }
      this.itemName = data?.name || 'New';
      this.loadCreativeCrumbs();
      return;
    }

    if (!type) {
      this.itemName = data?.name || _.get(this.model, ['lineItem', 'name']) || data?.fileName || 'New';
    }
    if (!this.crumbParams?.length) {
      return;
    }

    if (type) {
      const crumbIndex = _.findIndex(this.crumbs, (crumb) => {
        return crumb.objectType.type === type.type && !crumb.id;
      });
      if (crumbIndex > -1) {
        const updated = [...this.crumbs];
        updated[crumbIndex] = updateCrumb(this.crumbCtrl, type, data);
        this.crumbs = updated;
      }
      return;
    }

    const paramOnResponse = this.objectType?.paramOnResponse;
    const newCrumbs: DefaultItemTitleCrumb[] = [];
    const theCrumbs = _.filter(this.crumbParams, (c) => {
      return c.func ? this.localUserPermissions[c.func]() : true;
    });
    const rawCrumbData = paramOnResponse ? data[paramOnResponse] : data;
    const crumbData = this.objectTypeKey === 'LineItem' ? this.lineItemCrumbData(rawCrumbData) : rawCrumbData;
    _.forEach(theCrumbs, (lCrumb) => {
      calculateCrumbs(this.crumbCtrl, lCrumb.type || lCrumb, crumbData, newCrumbs);
    });
    this.crumbs = newCrumbs;
  }

  private initVariantCrumbs(): void {
    if (this.titleVariant === 'creative') {
      this.loadCreativeCrumbs();
    } else if (this.titleVariant === 'folio') {
      this.applyFolioCrumbs(this.model);
    } else if (this.titleVariant === 'creative-set') {
      this.loadCreativeSetParentCrumbs(this.model);
    } else if (this.objectTypeKey === 'LineItem') {
      this.applyLineItemCrumbs(this.model);
    }
  }

  private lineItemCrumbData(data: any): any {
    if (!data) {
      return null;
    }
    return {
      ...data,
      advertiser: data.advertiser || this.advertiserFromParam
    };
  }

  private applyLineItemCrumbs(data: any): void {
    if (!this.crumbParams?.length || !this.crumbCtrl) {
      return;
    }
    const crumbData = this.lineItemCrumbData(data);
    if (!crumbData) {
      return;
    }
    const newCrumbs: DefaultItemTitleCrumb[] = [];
    const theCrumbs = _.filter(this.crumbParams, (c) => {
      return c.func ? this.localUserPermissions[c.func]() : true;
    });
    _.forEach(theCrumbs, (lCrumb) => {
      calculateCrumbs(this.crumbCtrl, lCrumb.type || lCrumb, crumbData, newCrumbs);
    });
    this.crumbs = newCrumbs;
  }

  private resolveItemName(source: any): string {
    const paramOnResponse = this.objectType?.paramOnResponse;
    if (paramOnResponse) {
      return _.get(source, [paramOnResponse, 'name']) || 'New';
    }
    return source?.name || source?.fileName || 'New';
  }

  private applyFolioCrumbs(data: any): void {
    if (!this.localUserPermissions.canBeShownAdvertiser()) {
      this.crumbs = [];
      return;
    }
    const newCrumbs: DefaultItemTitleCrumb[] = [];
    calculateCrumbs(this.crumbCtrl, 'Advertiser', data, newCrumbs);
    this.crumbs = newCrumbs;
  }

  private applyCreativeSetCrumbs(crumbSource: any): void {
    const newCrumbs: DefaultItemTitleCrumb[] = [];
    _.forEach(['Advertiser', 'Folio'], (lCrumb) => {
      calculateCrumbs(this.crumbCtrl, lCrumb, crumbSource, newCrumbs);
    });
    this.crumbs = newCrumbs;
  }

  private loadCreativeSetParentCrumbs(data: any): void {
    const folio = _.get(data, ['folio']) || this.folioFromParam;
    const advertiser = _.get(data, ['advertiser']) || this.advertiserFromParam;
    if (_.get(folio, 'id') || _.get(advertiser, 'id')) {
      this.applyCreativeSetCrumbs({folio: folio, advertiser: advertiser});
    }
    if (!this.persisted || !this.model?.id) {
      return;
    }
    if (_.get(folio, 'id') && _.get(advertiser, 'id')) {
      return;
    }
    this.creativeSetInfo.get({id: this.model.id}, (cs: any) => {
      this.ngZone.run(() => {
        this.applyCreativeSetCrumbs({
          folio: _.get(cs, 'folio') || folio,
          advertiser: _.get(cs, 'advertiser') || advertiser
        });
        this.cdr.markForCheck();
      });
    });
  }

  private loadCreativeCrumbs(): void {
    if (this.creativeCrumbsLoaded && this.crumbs.length > 0) {
      return;
    }

    const lineItemInfo = this.lineItemInfo || {};
    const parentObject = lineItemInfo.id ? lineItemInfo : this.model?.lineItem ? this.model.lineItem : this.model?.creativeSet;
    if (!parentObject?.id) {
      this.crumbs = [];
      return;
    }

    if (lineItemInfo.fromLineItem || _.get(this.model, ['lineItem', 'id'])) {
      this.dataViewResource.getFromCache(parentObject.id).then((dataViewObj: any) => {
        this.ngZone.run(() => {
          const newCrumbs: DefaultItemTitleCrumb[] = [];
          let crumbs = ['Advertiser', 'Order', 'LineItem'];
          if (this.localUserPermissions.isSelfService() || this.localUserPermissions.isAxPublisherOnly()) {
            crumbs = ['LineItem'];
          }
          _.forEach(crumbs, (lCrumb) => {
            calculateCrumbs(this.crumbCtrl, lCrumb, dataViewObj, newCrumbs);
          });
          this.modelComms?.relDataUpdated(dataViewObj.lineItem, OBJECT_TYPE.LineItem);
          this.modelComms?.relDataUpdated(dataViewObj.advertiser, OBJECT_TYPE.Advertiser);
          this.crumbs = newCrumbs;
          this.creativeCrumbsLoaded = true;
          this.cdr.markForCheck();
        });
      });
      return;
    }

    if (lineItemInfo.fromCreativeSet || _.get(this.model, ['creativeSet', 'id'])) {
      const doCreativeSetCrumbs = (dataViewObj: any) => {
        const newCrumbs: DefaultItemTitleCrumb[] = [];
        _.forEach(['Advertiser', 'Folio', 'CreativeSet'], (lCrumb) => {
          calculateCrumbs(this.crumbCtrl, lCrumb, dataViewObj, newCrumbs);
        });
        this.modelComms?.relDataUpdated(dataViewObj.creativeSet, OBJECT_TYPE.CreativeSet);
        this.modelComms?.relDataUpdated(dataViewObj.folio, OBJECT_TYPE.Folio);
        this.modelComms?.relDataUpdated(dataViewObj.advertiser, OBJECT_TYPE.Advertiser);
        this.crumbs = newCrumbs;
        this.creativeCrumbsLoaded = true;
        this.cdr.markForCheck();
      };

      if (_.get(this.model, ['creativeSet', 'id']) && !_.get(lineItemInfo, ['advertiser', 'id'])) {
        this.creativeSetInfo.get({id: this.model.creativeSet.id}, (cs: any) => {
          this.ngZone.run(() => {
            doCreativeSetCrumbs({
              creativeSet: this.model.creativeSet,
              folio: cs.folio,
              advertiser: cs.advertiser
            });
          });
        });
      } else {
        doCreativeSetCrumbs({
          creativeSet: {
            id: lineItemInfo.id,
            name: lineItemInfo.name
          },
          advertiser: {
            id: lineItemInfo.advertiserId,
            name: lineItemInfo.advertiserName
          }
        });
      }
      return;
    }

    this.crumbs = [];
  }
}
